/**
 * Ciclo de vida del preloader de arranque.
 *
 * El preloader es HTML plano en `index.html`, no un componente Angular, a
 * propósito: un componente solo existe después de que el bundle se descarga y
 * evalúa, que es exactamente el hueco que queremos cubrir. Aquí solo se
 * conduce su salida.
 *
 * Reglas de la salida:
 *  - Un mínimo de duración, para que en arranques rápidos no parpadee.
 *  - Espera a la primera navegación completada, que es cuando hay algo real
 *    pintado detrás; no al arranque de Angular, que solo garantiza el shell.
 *  - Red de seguridad por si la navegación nunca ocurre: el usuario no puede
 *    quedarse mirando el preloader indefinidamente.
 */

/** Tiempo mínimo en pantalla: evita el parpadeo en arranques rápidos. */
const MIN_VISIBLE_MS = 550;

/**
 * Si la app no avisa de estar lista en este plazo, se destapa igual.
 *
 * Es una última red, no el camino normal: la salida real la dispara la
 * navegación completada. Va holgada a propósito, porque un umbral corto no
 * distingue "arranque lento" de "arranque roto" y, al destaparse sobre un
 * outlet todavía vacío, deja al usuario mirando una página en blanco, que es
 * justo lo que este preloader evita.
 */
const SAFETY_TIMEOUT_MS = 30_000;

/** Cadencia con la que avanzan los mensajes de estado. */
const STATUS_INTERVAL_MS = 2_600;

/**
 * Mensajes rotativos, en clave de alerta temprana: describen lo que la app
 * está haciendo en realidad (vigilar, traer capas, cruzar alertas) en vez de
 * un "cargando…" vacío, que es lo que hace que una espera larga se note
 * larga. El último mensaje no se repite: la rotación se detiene ahí, porque
 * envolver al principio tras "Listo" se lee como un ciclo roto.
 */
const STATUS_LINES = [
  "Iniciando plataforma",
  "Conectando visor territorial",
  "Cargando capas oficiales",
  "Sincronizando alertas satelitales",
  "Listo",
] as const;

const PRELOADER_ID = "app-preloader";
const STATUS_ID = "app-preloader-status";

/** Instante en que este módulo evaluó, es decir, en que ya hay preloader. */
const STARTED_AT = Date.now();

let statusTimer: number | undefined;
let safetyTimer: number | undefined;
/** Segunda llamada a dismiss mientras la primera aún espera: no reprogramar. */
let dismissPending = false;

/** Actualiza el texto de estado con un fade suave y fluido. */
function setStatusMessage(element: HTMLElement, text: string): void {
  element.style.opacity = "0";
  element.style.transform = "translateY(2px)";
  window.setTimeout(() => {
    element.textContent = text;
    element.style.opacity = "1";
    element.style.transform = "translateY(0)";
  }, 160);
}

/** Arranca la rotación de mensajes. Idempotente. */
function startStatusRotation(): void {
  if (statusTimer !== undefined) return;

  const status = document.getElementById(STATUS_ID);
  if (!status) return;

  let index = 0;
  status.textContent = STATUS_LINES[index];
  statusTimer = window.setInterval(() => {
    if (index >= STATUS_LINES.length - 1) {
      window.clearInterval(statusTimer);
      statusTimer = undefined;
      return;
    }
    index += 1;
    setStatusMessage(status, STATUS_LINES[index]);
  }, STATUS_INTERVAL_MS);
}

/** Marca la salida y retira el nodo cuando la transición termina. */
function hide(preloader: HTMLElement): void {
  preloader.dataset["done"] = "1";
  preloader.addEventListener("transitionend", () => preloader.remove(), {
    once: true,
  });
  // Red propia: si no hay transición que escuchar (reduced-motion, pestaña en
  // segundo plano), el nodo se retira igual y no queda nada en el DOM.
  window.setTimeout(() => preloader.remove(), 600);
}

/**
 * Destapa el preloader. Se puede llamar varias veces (navegación + red de
 * seguridad): solo la primera cuenta; el resto se ignora con `dismissPending`
 * antes de tocar timers, para que dos llamadas seguidas no programen dos
 * ocultamientos ni reinicien el tiempo mínimo.
 */
export function dismissAppPreloader(): void {
  if (dismissPending) return;
  dismissPending = true;

  if (statusTimer !== undefined) {
    window.clearInterval(statusTimer);
    statusTimer = undefined;
  }
  // La red de seguridad ya no hace falta: si queda pendiente, su callback
  // volvería a llamar a dismiss con el preloader fuera del DOM.
  if (safetyTimer !== undefined) {
    window.clearTimeout(safetyTimer);
    safetyTimer = undefined;
  }

  const preloader = document.getElementById(PRELOADER_ID);
  // Ausente (ya retirado): nada que hacer.
  if (!preloader) return;

  // El mínimo se mide contra el arranque del módulo, no contra esta llamada:
  // si el preloader ya cumplió su tiempo visible, se va sin esperar nada más.
  const remaining = MIN_VISIBLE_MS - (Date.now() - STARTED_AT);
  if (remaining <= 0) {
    hide(preloader);
    return;
  }

  window.setTimeout(() => hide(preloader), remaining);
}

/**
 * Prepara el preloader y arma la red de seguridad. Se invoca al cargar el
 * módulo para que los mensajes empiecen a correr de inmediato, sin esperar a
 * que Angular arranque.
 */
export function initAppPreloader(): void {
  // Idempotente: una segunda llamada no debe programar un segundo timeout
  // de seguridad (el primero quedaría huérfano y sobreescribiría la variable).
  if (safetyTimer !== undefined) return;
  if (!document.getElementById(PRELOADER_ID)) return;

  startStatusRotation();

  // El preloader nunca debe ser la última imagen de la pantalla: si algo se
  // rompe antes de que la app avise, se destapa igual y el usuario ve la app
  // rota en vez de un indicador eterno.
  safetyTimer = window.setTimeout(() => dismissAppPreloader(), SAFETY_TIMEOUT_MS);
}
